import { components } from "@/__openapi__/backend/schema";
import {
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextOverflow,
  MDSButton,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSUserAvatar,
  MDSChip,
  MDSChipVariant,
  MDSMenuItemVariant,
  MDSMenuItemIconVariant,
} from "@/mds";
import { formatCalendarEventDate, formatCalendarEventTime } from "@/domains/calendar/formating";
import { windowModule } from "@/modules/window";
import {
  StyledContainer,
  StyledLabel,
  StyledRow,
  StyledValue,
  StyledDescriptionContainer,
  StyledShowMore,
  StyledGuestsContainer,
  StyledGuestsHeader,
  StyledGuestMenuItem,
  StyledAttachmentsContainer,
  ButtonContainer,
} from "./floating-calendar-event-preview.style";
import { useState } from "react";

type CalendarEventOrganizer = components["schemas"]["CalendarEventOrganizer"];
type CalendarEventAttendee = components["schemas"]["CalendarEventAttendee"];
type CalendarEventAttachment = components["schemas"]["CalendarEventAttachment"];

interface FloatingCalendarEventPreviewViewProps {
  startTime?: string;
  endTime?: string;
  description?: string;
  isAllDay: boolean;
  host?: CalendarEventOrganizer | null;
  guests?: CalendarEventAttendee[];
  attachments?: CalendarEventAttachment[];
  htmlLink?: string | null;
  meetLink?: string | null;
}

export const FloatingCalendarEventPreviewView = ({
  startTime,
  endTime,
  description,
  isAllDay,
  host,
  guests,
  attachments,
  htmlLink,
  meetLink,
}: FloatingCalendarEventPreviewViewProps) => {
  const [showMultilineDescription, setShowMultilineDescription] = useState<boolean>(false);
  const [isGuestsCollapsed, setIsGuestsCollapsed] = useState<boolean>(false);
  const timeText = formatCalendarEventTime(startTime, endTime, isAllDay);
  const dateText = startTime ? formatCalendarEventDate(startTime) : isAllDay ? "All day" : "";
  const hostDisplayName = host?.display_name || host?.email;

  return (
    <StyledContainer>
      {dateText && (
        <StyledRow>
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Date
          </StyledLabel>
          <StyledValue
            variant={MDSTextVariant.TextS}
            color={MDSTextColor.Primary}
            numberOfLines={1}
            overflow={MDSTextOverflow.Ellipsis}
          >
            {dateText}
          </StyledValue>
        </StyledRow>
      )}

      {timeText && (
        <StyledRow>
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Time
          </StyledLabel>
          <StyledValue
            variant={MDSTextVariant.TextS}
            color={MDSTextColor.Primary}
            numberOfLines={1}
            overflow={MDSTextOverflow.Ellipsis}
          >
            {timeText}
          </StyledValue>
        </StyledRow>
      )}

      {description && (
        <StyledRow>
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Description
          </StyledLabel>
          <StyledDescriptionContainer>
            <StyledValue
              variant={MDSTextVariant.TextS}
              color={MDSTextColor.Primary}
              numberOfLines={showMultilineDescription ? undefined : 1}
              overflow={showMultilineDescription ? undefined : MDSTextOverflow.Ellipsis}
            >
              {description}
            </StyledValue>
            <StyledShowMore
              variant={MDSTextVariant.TextXs}
              weight={MDSTextWeight.Medium}
              color={MDSTextColor.Secondary}
              onClick={() => setShowMultilineDescription(!showMultilineDescription)}
            >
              Show {showMultilineDescription ? "less" : "more"}
            </StyledShowMore>
          </StyledDescriptionContainer>
        </StyledRow>
      )}

      {attachments && attachments.length > 0 && (
        <StyledRow align="center">
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Attachments
          </StyledLabel>
          <StyledValue variant={MDSTextVariant.TextS} color={MDSTextColor.Primary}>
            <StyledAttachmentsContainer>
              {attachments.map((attachment, index) => (
                <MDSChip
                  key={index}
                  onClick={() => windowModule.openInNewTab({ url: new URL(attachment.file_url) })}
                  variant={MDSChipVariant.Medium}
                  icon={MDSIconKind.File}
                >
                  {attachment.title || "Attachment"}
                </MDSChip>
              ))}
            </StyledAttachmentsContainer>
          </StyledValue>
        </StyledRow>
      )}

      {host && (
        <StyledRow align="center">
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Organizer
          </StyledLabel>
          <StyledValue variant={MDSTextVariant.TextS} color={MDSTextColor.Primary}>
            <StyledGuestMenuItem
              id="host"
              label={hostDisplayName ?? ""}
              subtitle={host.email}
              variant={MDSMenuItemVariant.TwoLine}
              icon={() => <MDSUserAvatar displayName={hostDisplayName} size={24} />}
              iconVariant={MDSMenuItemIconVariant.Avatar}
            />
          </StyledValue>
        </StyledRow>
      )}

      {guests && guests.length > 0 && (
        <StyledRow>
          <StyledLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            color={MDSTextColor.Secondary}
          >
            Guests
          </StyledLabel>
          <StyledValue variant={MDSTextVariant.TextS} color={MDSTextColor.Primary}>
            <StyledGuestsHeader onClick={() => setIsGuestsCollapsed(!isGuestsCollapsed)}>
              <MDSIcon
                kind={isGuestsCollapsed ? MDSIconKind.CaretRight : MDSIconKind.CaretDown}
                color={MDSIconColor.Secondary}
                size={11}
              />
              <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Primary}>
                {guests.length} {guests.length === 1 ? "guest" : "guests"}
              </MDSText>
            </StyledGuestsHeader>
            {!isGuestsCollapsed && (
              <StyledGuestsContainer>
                {guests.map((guest, index) => {
                  const displayName = (guest.display_name || guest.email) ?? undefined;
                  return (
                    <StyledGuestMenuItem
                      key={index}
                      id={`guest-${index}`}
                      label={displayName ?? ""}
                      subtitle={guest.email}
                      variant={MDSMenuItemVariant.TwoLine}
                      icon={() => <MDSUserAvatar displayName={displayName} size={24} />}
                      iconVariant={MDSMenuItemIconVariant.Avatar}
                    />
                  );
                })}
              </StyledGuestsContainer>
            )}
          </StyledValue>
        </StyledRow>
      )}

      {(meetLink || htmlLink) && (
        <ButtonContainer>
          {htmlLink && (
            <MDSButton onClick={() => windowModule.openInNewTab({ url: new URL(htmlLink) })}>
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.GoogleCalendar} />
              Open in calendar
            </MDSButton>
          )}
          {meetLink && (
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              onClick={() => windowModule.openInNewTab({ url: new URL(meetLink) })}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Video} />
              Join meeting
            </MDSButton>
          )}
        </ButtonContainer>
      )}
    </StyledContainer>
  );
};
